import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { todayISO, isOverdue, formatDate } from "@/lib/tp";
import TaskRow from "@/components/TaskRow";
import { CalendarClock, CheckCircle2, Zap, CalendarPlus, ChevronDown, Users, Plus, CheckSquare, Copy, Trash2, CalendarDays, X } from "lucide-react";
import { isCliente, isHiddenUser } from "@/lib/roles";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TaskModal from "@/components/TaskModal";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

function toISOFromDate(d) {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Una task completata resta visibile in "Il mio lavoro" fino a fine giornata (ora locale).
function isCompletedToday(t) {
  if (t.status !== "completato" || !t.updated_at) return false;
  const d = new Date(t.updated_at);
  if (isNaN(d)) return false;
  const local = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return local === todayISO();
}
const isActiveOrCompletedToday = (t) => t.status !== "completato" || isCompletedToday(t);

// Ordine "Il mio lavoro": urgenti in alto, poi da fare, poi in corso, completate in basso.
function workOrderRank(t) {
  if (t.status === "completato") return 3;
  if (t.priority === 5) return 0; // urgente (non completata)
  if (t.status === "in_corso") return 2;
  return 1; // da_fare
}
const byWorkOrder = (a, b) => {
  const r = workOrderRank(a) - workOrderRank(b);
  if (r !== 0) return r;
  return (a.due_date || "").localeCompare(b.due_date || "");
};

export default function MyWork() {
  const { tasks, users, loading, updateTask, addTask } = useData();
  const { user } = useAuth();
  const [selectedUserId, setSelectedUserId] = useState(user.id);
  const [taskOpen, setTaskOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [moveCalOpen, setMoveCalOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const cliente = isCliente(user);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const exitSelect = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  // Utenti selezionabili nel filtro (team + admin, escluso il super-admin salvo se sei tu).
  const teamOptions = useMemo(
    () =>
      users
        .filter(
          (u) =>
            u.role !== "cliente" &&
            (!isHiddenUser(u) || u.id === user.id),
        )
        .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [users, user.id],
  );

  const targetId = cliente ? null : selectedUserId || user.id;
  const viewUser = users.find((u) => u.id === targetId) || user;
  const isViewingOther = !cliente && viewUser.id !== user.id;

  // Cliente-role sees ALL tasks of their scoped clients (backend already filters by scope).
  // Team members see the tasks assigned to the selected user (default: themselves).
  const mine = useMemo(
    () =>
      cliente
        ? tasks
        : tasks.filter((t) => t.assignee_ids?.includes(targetId)),
    [tasks, cliente, targetId],
  );

  const overdue = useMemo(
    () =>
      mine.filter(
        (t) => isOverdue(t.due_date) && isActiveOrCompletedToday(t),
      ).sort(byWorkOrder),
    [mine],
  );

  const dueToday = useMemo(
    () =>
      mine.filter(
        (t) => t.due_date === todayISO() && isActiveOrCompletedToday(t),
      ).sort(byWorkOrder),
    [mine],
  );

  const upcoming = useMemo(
    () =>
      mine
        .filter(
          (t) =>
            t.due_date &&
            t.due_date > todayISO() &&
            isActiveOrCompletedToday(t),
        )
        .sort(byWorkOrder),
    [mine],
  );

  const completedToday = useMemo(
    () =>
      mine.filter((t) => t.due_date === todayISO() && t.status === "completato").length,
    [mine],
  );

  // "Da svolgere" oggi: esclude le completate, conta solo da_fare / in_corso.
  const dueTodayTodo = useMemo(
    () => dueToday.filter((t) => t.status !== "completato").length,
    [dueToday],
  );

  const selectedTasks = useMemo(
    () => mine.filter((t) => selectedIds.has(t.id)),
    [mine, selectedIds],
  );
  const selCount = selectedTasks.length;

  const bulkDuplicate = async () => {
    if (!selCount || bulkBusy) return;
    setBulkBusy(true);
    try {
      for (const t of selectedTasks) {
        await addTask({
          title: t.title,
          description: t.description || "",
          client_id: t.client_id,
          assignee_ids: t.assignee_ids || [],
          due_date: t.due_date,
          priority: t.priority,
          status: "da_fare",
          notify_user_id: t.notify_user_id || null,
          checklist: (Array.isArray(t.checklist) ? t.checklist : []).map((c) => ({ ...c, done: false })),
          attachments: t.attachments || [],
          duplicate_on_complete: !!t.duplicate_on_complete,
        });
      }
      toast.success(`${selCount} task duplicate`);
      exitSelect();
    } catch {
      toast.error("Errore durante la duplicazione");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDelete = async () => {
    if (!selCount || bulkBusy) return;
    setBulkBusy(true);
    try {
      await Promise.all(selectedTasks.map((t) => updateTask(t.id, { trashed: true })));
      toast.success(`${selCount} task spostate nel Cestino`);
      exitSelect();
    } catch {
      toast.error("Errore durante l'eliminazione");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkMove = async (d) => {
    if (!d || !selCount || bulkBusy) return;
    const due = toISOFromDate(d);
    setBulkBusy(true);
    try {
      await Promise.all(selectedTasks.map((t) => updateTask(t.id, { due_date: due })));
      toast.success(`${selCount} task spostate al ${formatDate(due)}`);
      setMoveCalOpen(false);
      exitSelect();
    } catch {
      toast.error("Errore durante lo spostamento");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-6 tp-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            Ciao {user.name.split(" ")[0]}
          </div>
          <div className="flex items-center gap-3 mt-1">
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white">
              Il mio lavoro
            </h1>
            <Button
              data-testid="mywork-add-task-button"
              onClick={() => setTaskOpen(true)}
              className="lg:hidden tp-brand-btn h-9 rounded-full px-3 text-sm shrink-0"
            >
              Task <Plus size={15} className="ml-1" />
            </Button>
          </div>
          <p className="text-sm text-zinc-400 mt-1">
            {isViewingOther
              ? `Stai visualizzando il lavoro di ${viewUser.name}.`
              : "Task assegnate a te: scadute, oggi e prossimi giorni."}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {!cliente && (
            <div className="flex items-center gap-2 shrink-0" data-testid="mywork-user-filter">
              <Users size={15} className="text-zinc-500" />
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger
                  data-testid="mywork-user-select-trigger"
                  className="w-[220px] tp-glass border-white/10 text-white"
                >
                  <SelectValue placeholder="Seleziona utente" />
                </SelectTrigger>
                <SelectContent className="tp-glass-strong border-white/10 text-white max-h-72">
                  {teamOptions.map((u) => (
                    <SelectItem
                      key={u.id}
                      value={u.id}
                      data-testid={`mywork-user-option-${u.id}`}
                    >
                      {u.id === user.id ? `${u.name} (io)` : u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <div className="tp-glass rounded-2xl p-4 sm:p-5 relative overflow-hidden tp-noise">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500">
            <Zap size={12} className="text-amber-400" /> Oggi
          </div>
          <div className="text-3xl font-semibold text-white mt-2 tabular-nums">{dueTodayTodo}</div>
          <div className="text-xs text-zinc-500">task da svolgere</div>
        </div>
        <div className="tp-glass rounded-2xl p-4 sm:p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500">
            <CheckCircle2 size={12} className="text-emerald-400" /> Fatte
          </div>
          <div className="text-3xl font-semibold text-white mt-2 tabular-nums">{completedToday}</div>
          <div className="text-xs text-zinc-500">completate oggi</div>
        </div>
        <div className="tp-glass rounded-2xl p-4 sm:p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500">
            <CalendarClock size={12} className="text-red-400" /> Scadute
          </div>
          <div className="text-3xl font-semibold text-white mt-2 tabular-nums">{overdue.length}</div>
          <div className="text-xs text-zinc-500">task in ritardo</div>
        </div>
      </div>

      {/* Sections */}
      {/* Bulk actions bar — fixed via portal so no transformed ancestor affects it */}
      {selectMode && selCount > 0 &&
        createPortal(
          <div
            data-testid="mywork-bulk-bar"
            className="fixed left-1/2 -translate-x-1/2 z-[60] flex flex-wrap items-center justify-center gap-2 rounded-2xl tp-glass-strong border border-fuchsia-400/30 shadow-2xl shadow-black/50 px-3 py-2 w-[calc(100%-1.5rem)] max-w-xl animate-in fade-in slide-in-from-top-2"
            style={{ top: "calc(env(safe-area-inset-top) + 4.75rem)" }}
          >
          <span className="text-sm text-white px-2 tabular-nums" data-testid="mywork-bulk-count">
            {selCount} selezionate
          </span>
          <div className="h-5 w-px bg-white/15" />
          <Button data-testid="mywork-bulk-duplicate" onClick={bulkDuplicate} disabled={bulkBusy}
            variant="ghost" className="h-9 rounded-xl text-zinc-100 hover:bg-white/10">
            <Copy size={15} className="mr-1.5" /> Duplica
          </Button>
          <Button data-testid="mywork-bulk-move" onClick={() => setMoveCalOpen(true)} disabled={bulkBusy}
            variant="ghost" className="h-9 rounded-xl text-zinc-100 hover:bg-white/10">
            <CalendarDays size={15} className="mr-1.5" /> Sposta al…
          </Button>
          <Button data-testid="mywork-bulk-delete" onClick={bulkDelete} disabled={bulkBusy}
            variant="ghost" className="h-9 rounded-xl text-red-300 hover:bg-red-500/15 hover:text-red-200">
            <Trash2 size={15} className="mr-1.5" /> Elimina
          </Button>
          <button data-testid="mywork-bulk-close" onClick={exitSelect}
            className="ml-auto p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-white/10">
            <X size={16} />
          </button>
        </div>,
          document.body,
        )}

      {overdue.length > 0 && (
        <Section
          title="Scadute"
          tasks={overdue}
          loading={false}
          testId="section-scadute"
          accent="red"
          selectable={selectMode}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onToggleSelectMode={() => (selectMode ? exitSelect() : setSelectMode(true))}
        />
      )}

      <Section
        title="In scadenza oggi"
        emptyLabel={
          completedToday > 0
            ? "Tutte fatte! Ottimo lavoro."
            : "Nessuna task in scadenza oggi. Bella lì!"
        }
        tasks={dueToday}
        loading={loading}
        testId="section-today"
        icon={<Zap size={12} className="text-amber-400" />}
        highlight="today"
        selectable={selectMode}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectMode={() => (selectMode ? exitSelect() : setSelectMode(true))}
      />

      <Section
        title="Prossimi giorni"
        emptyLabel="Nessuna task in arrivo. Goditi la calma."
        tasks={upcoming}
        loading={false}
        testId="section-upcoming"
        icon={<CalendarPlus size={12} className="text-blue-300" />}
        collapsible
        selectable={selectMode}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectMode={() => (selectMode ? exitSelect() : setSelectMode(true))}
      />

      {/* Move-to-date calendar */}
      <Dialog open={moveCalOpen} onOpenChange={setMoveCalOpen}>
        <DialogContent className="tp-glass-strong border-white/10 text-white w-auto max-w-fit" data-testid="mywork-move-calendar">
          <DialogHeader>
            <DialogTitle className="text-white text-base">Sposta {selCount} task al giorno…</DialogTitle>
          </DialogHeader>
          <Calendar mode="single" onSelect={bulkMove} className="rounded-md" />
        </DialogContent>
      </Dialog>

      <TaskModal open={taskOpen} onOpenChange={setTaskOpen} />
    </div>
  );
}

function Section({ title, tasks, loading, emptyLabel, testId, accent, icon, highlight, collapsible, selectable, selectedIds, onToggleSelect, onToggleSelectMode }) {
  const [collapsed, setCollapsed] = useState(false);
  const borderCls =
    highlight === "today"
      ? "border-amber-400/40 shadow-[0_0_0_1px_rgba(251,191,36,0.15)] shadow-amber-400/10"
      : "";
  return (
    <section
      className={`tp-glass rounded-3xl overflow-hidden border ${borderCls || "border-white/10"}`}
      data-testid={testId}
    >
      <div
        className={`px-5 sm:px-6 py-4 border-b border-white/5 flex items-center gap-3 ${
          collapsible ? "cursor-pointer select-none" : ""
        }`}
        onClick={collapsible ? () => setCollapsed((v) => !v) : undefined}
        data-testid={collapsible ? `${testId}-toggle` : undefined}
      >
        {icon}
        <h2 className="text-base font-semibold text-white">{title}</h2>
        {accent === "red" && (
          <span className="h-1.5 w-1.5 rounded-full bg-red-500 tp-pulse-dot" />
        )}
        <span className="ml-auto text-xs text-zinc-500 tabular-nums">{tasks.length}</span>
        {onToggleSelectMode && tasks.length > 0 && (
          <button
            type="button"
            data-testid={`${testId}-select-toggle`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelectMode();
            }}
            title={selectable ? "Annulla selezione" : "Seleziona task"}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              selectable
                ? "bg-fuchsia-500/15 border border-fuchsia-400/40 text-fuchsia-200"
                : "border border-white/10 text-zinc-300 hover:text-white hover:bg-white/5"
            }`}
          >
            <CheckSquare size={13} />
            {selectable ? "Annulla" : "Seleziona"}
          </button>
        )}
        {collapsible && (
          <ChevronDown
            size={16}
            className={`text-zinc-400 transition-transform ${collapsed ? "-rotate-90" : ""}`}
          />
        )}
      </div>
      {!collapsed && (
        <div className="px-2 sm:px-3 py-2">
          {loading ? (
            <div className="px-4 py-6 text-sm text-zinc-500">Caricamento…</div>
          ) : tasks.length === 0 ? (
            <div className="px-4 py-8 text-sm text-zinc-500 italic text-center">
              {emptyLabel || "Nessun elemento."}
            </div>
          ) : (
            tasks.map((t) => {
              const checked = selectable && selectedIds?.has(t.id);
              return (
                <div
                  key={t.id}
                  data-testid={`my-work-item-${t.id}`}
                  className={`flex items-center gap-2 rounded-xl transition-colors ${
                    checked ? "bg-fuchsia-500/10 ring-1 ring-fuchsia-400/30" : ""
                  }`}
                >
                  {selectable && (
                    <label className="pl-2 shrink-0 cursor-pointer" title="Seleziona">
                      <input
                        type="checkbox"
                        checked={!!checked}
                        onChange={() => onToggleSelect(t.id)}
                        data-testid={`mywork-select-${t.id}`}
                        className="accent-fuchsia-500 h-5 w-5"
                      />
                    </label>
                  )}
                  <div className="flex-1 min-w-0">
                    <TaskRow task={t} showClient />
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </section>
  );
}
